import SvgArrowLeft from '@sats-group/icons/18/arrow-left';
import SvgArrowRight from '@sats-group/icons/18/arrow-right';
import Button from '@sats-group/ui-lib/react/button';
import Text from '@sats-group/ui-lib/react/text';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { mod } from '~/client/helpers/add-bem-modifiers';
import type { Carousel as Props } from './carousel.types';
import { variants } from './carousel.types';

type CarouselComponent = {
  <T extends { tall?: boolean }>(
    props: Props<T> & { entryRenderer: React.FC<T> }
  ): React.ReactNode;
  variants: typeof variants;
};

const computeItemTargets = (
  carouselView: HTMLDivElement,
  carouselTrack: HTMLOListElement
) => {
  const items = Array.from(carouselTrack.children) as HTMLElement[];
  const firstItem = items[0];
  const lastItem = items.at(-1);
  if (!firstItem || !lastItem) {
    return { targets: [0], trailingSpace: 0 };
  }

  if (
    lastItem.offsetLeft + lastItem.offsetWidth <=
    carouselView.clientWidth + 1
  ) {
    return { targets: [0], trailingSpace: 0 };
  }

  const gap = Number.parseFloat(getComputedStyle(carouselTrack).columnGap) || 0;
  const targets = items.map(item => item.offsetLeft - firstItem.offsetLeft);
  const trailingSpace = Math.max(
    carouselView.clientWidth -
      lastItem.offsetWidth -
      gap -
      firstItem.offsetLeft,
    0
  );

  return { targets, trailingSpace };
};

/* SPEC: https://www.figma.com/design/dQeQgh6sANMhWBUNtgXQNN/satsweb-components?node-id=2231-21321&t=FlxIq9WpMSB895V3-0 */
const Carousel: CarouselComponent = ({
  description,
  entries,
  entryRenderer,
  id,
  labels,
  title,
  variant = variants.contained,
}) => {
  const [itemTargets, setItemTargets] = useState<number[]>([]);
  const [trailingSpace, setTrailingSpace] = useState(0);
  const [currentItem, setCurrentItem] = useState(0);
  const view = useRef<HTMLDivElement>(null);
  const track = useRef<HTMLOListElement>(null);

  const updateState = useCallback((scrollLeft?: number) => {
    const carouselView = view.current;
    const carouselTrack = track.current;
    if (!carouselView || !carouselTrack) {
      return;
    }

    const { targets, trailingSpace: trailing } = computeItemTargets(
      carouselView,
      carouselTrack
    );
    const currentScrollLeft = scrollLeft ?? carouselView.scrollLeft;
    let nearestDistance = Number.POSITIVE_INFINITY;
    let nearestItem = 0;

    targets.forEach((target, index) => {
      const distance = Math.abs(target - currentScrollLeft);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestItem = index;
      }
    });

    setItemTargets(targets);
    setTrailingSpace(trailing);
    setCurrentItem(nearestItem);
  }, []);

  const goToItem = (item: number) => {
    const target = itemTargets[item];
    if (!view.current || target === undefined) {
      return;
    }

    setCurrentItem(item);
    view.current.scrollTo({ left: target });
  };

  const goToNext = () =>
    goToItem(Math.min(currentItem + 1, itemTargets.length - 1));

  const goToPrevious = () => goToItem(Math.max(currentItem - 1, 0));

  useEffect(() => {
    if (!view.current) {
      return;
    }

    const carouselView = view.current;
    const observer = new ResizeObserver(() => updateState());
    observer.observe(carouselView);
    updateState();

    return () => observer.unobserve(carouselView);
  }, [entries.length, updateState]);

  const EntryRenderer = entryRenderer;
  const canScroll = itemTargets.length > 1;
  const titleId = title ? `${id}-title` : undefined;

  return (
    <section aria-labelledby={titleId} className={mod('carousel', variant)}>
      <div className="carousel__content">
        <div className="carousel__text">
          {title ? (
            <Text
              elementName="h2"
              id={titleId}
              size={Text.sizes.headline1}
              theme={Text.themes.emphasis}
              tight
            >
              {title}
            </Text>
          ) : null}
          {description ? (
            <Text className="carousel__description" elementName="p">
              {description}
            </Text>
          ) : null}
        </div>
        <div className="carousel__slider">
          <div className="carousel__entries">
            <div
              aria-labelledby={titleId}
              className="carousel__view"
              data-overflowing={trailingSpace > 0 ? '' : undefined}
              id={id}
              onScrollEnd={() => updateState()}
              ref={view}
              role="group"
              style={
                {
                  '--carousel-trailing-space': `${trailingSpace}px`,
                } as React.CSSProperties
              }
              tabIndex={0}
            >
              <ol className="carousel__track" ref={track} role="list">
                {entries.map(entry => (
                  <li
                    className="carousel__entry"
                    data-key={entry.key}
                    key={entry.key}
                  >
                    <EntryRenderer {...entry.props} tall />
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
        {canScroll ? (
          <div className="carousel__markers">
            {itemTargets.map((_, index) => (
              <button
                aria-controls={id}
                aria-current={currentItem === index ? 'true' : undefined}
                aria-label={`${title ? `${title}: ` : ''}${index + 1} / ${itemTargets.length}`}
                className="carousel__marker"
                key={index}
                onClick={() => goToItem(index)}
                type="button"
              >
                <span aria-hidden="true" />
              </button>
            ))}
          </div>
        ) : null}
        {canScroll ? (
          <div className="carousel__arrows">
            <Button
              aria-controls={id}
              disabled={currentItem === 0}
              iconOnly
              leadingIcon={<SvgArrowLeft />}
              onClick={goToPrevious}
              size={Button.sizes.basic}
              text={labels.previous}
              variant={Button.variants.secondary}
            />
            <Button
              aria-controls={id}
              disabled={currentItem === itemTargets.length - 1}
              iconOnly
              leadingIcon={<SvgArrowRight />}
              onClick={goToNext}
              size={Button.sizes.basic}
              text={labels.next}
              variant={Button.variants.secondary}
            />
          </div>
        ) : null}
      </div>
    </section>
  );
};

Carousel.variants = variants;

export default Carousel;
